import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Grid,
  Card,
  CardContent,
  IconButton,
  Menu,
  MenuItem,
  Tabs,
  Tab,
  Link as MuiLink,
  Tooltip,
  Alert,
  Checkbox,
  FormControlLabel,
  Switch,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Select,
  FormControl,
  InputLabel,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  GetApp as GetAppIcon,
  Link as LinkIcon,
  QrCode as QrCodeIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Analytics as AnalyticsIcon,
  LocationOn as LocationOnIcon,
  MoreVert as MoreVertIcon,
  DataObject as DataObjectIcon,
  Assessment as AssessmentIcon,
  ContentCopy as ContentCopyIcon,
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';
import { usePermissions } from '../hooks/usePermissions';
import { useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import * as XLSX from 'xlsx';

interface SurveyLink {
  id: string;
  surveyId: string;
  surveyName: string;
  token: string;
  url: string;
  requiresAgentCode: boolean;
  expiresAt?: string;
  createdAt: string;
  submissionCount: number;
  isActive: boolean;
}

const DataCollection: React.FC = () => {
  const { user, session } = useAuth();
  const { hasPermission, isAdmin, isAgence, isSOBEBRA } = usePermissions();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [forms, setForms] = useState<any[]>([]);
  const [submissions, setSubmissions] = useState<any[]>([]);
  const [links, setLinks] = useState<SurveyLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [openLinkDialog, setOpenLinkDialog] = useState(false);
  const [openQRDialog, setOpenQRDialog] = useState<string | null>(null);
  const [openSubmissionDetailDialog, setOpenSubmissionDetailDialog] = useState(false);
  const [openExportDialog, setOpenExportDialog] = useState(false);
  const [exportFormat, setExportFormat] = useState<'excel' | 'csv'>('excel');
  const [selectedForm, setSelectedForm] = useState<any>(null);
  const [selectedSubmission, setSelectedSubmission] = useState<any>(null);
  const [selectedSubmissions, setSelectedSubmissions] = useState<Set<string>>(new Set());
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedFormId, setSelectedFormId] = useState<string | null>(null);
  const [linkFormData, setLinkFormData] = useState({
    surveyId: '',
    surveyName: '',
    requiresAgentCode: false,
    expiresAt: '',
  });
  const [createMode, setCreateMode] = useState<'select' | 'create'>('select');
  const [questionnaireData, setQuestionnaireData] = useState({
    name: '',
    description: '',
    questions: [] as Array<{
      id: string;
      type: 'text' | 'number' | 'select' | 'textarea' | 'checkbox' | 'radio';
      label: string;
      required: boolean;
      options?: string[];
      placeholder?: string;
    }>,
  });

  useEffect(() => {
    // Attendre que la session soit disponible avant de charger
    if (session) {
      fetchForms();
      fetchSubmissions();
      loadLinks();
    }
  }, [session]);

  const fetchForms = async () => {
    try {
      setLoading(true);
      const response = await api.forms.getAll();
      setForms(response.forms || []);
    } catch (error) {
      console.error('Erreur lors de la récupération des formulaires:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSubmissions = async () => {
    try {
      const response = await api.forms.getSubmissions();
      setSubmissions(response.submissions || []);
    } catch (error) {
      console.error('Erreur lors de la récupération des soumissions:', error);
    }
  };

  const loadLinks = () => {
    const mockLinks: SurveyLink[] = forms.map((form) => ({
      id: `link-${form.id}`,
      surveyId: form.id,
      surveyName: form.name,
      token: `token-${form.id}`,
      url: `${window.location.origin}/survey/token-${form.id}/${form.id}/token-${form.id}`,
      requiresAgentCode: form.requires_geolocation || false,
      expiresAt: undefined,
      createdAt: form.created_at,
      submissionCount: submissions.filter((s) => s.form_id === form.id).length,
      isActive: form.is_active === 1,
    }));
    setLinks(mockLinks);
  };

  useEffect(() => {
    loadLinks();
  }, [forms, submissions]);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, formId: string) => {
    setAnchorEl(event.currentTarget);
    setSelectedFormId(formId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedFormId(null);
  };

  const handleCreateLink = (form?: any) => {
    if (form) {
      setLinkFormData({
        surveyId: form.id,
        surveyName: form.name,
        requiresAgentCode: false,
        expiresAt: '',
      });
    }
    setOpenLinkDialog(true);
    handleMenuClose();
  };

  const handleViewAnalytics = (form: any) => {
    navigate(`/survey-analytics?formId=${form.id}`);
    handleMenuClose();
  };

  const handleShowQR = (linkId: string) => {
    setOpenQRDialog(linkId);
    handleMenuClose();
  };

  const handleViewSubmissionDetails = (submission: any) => {
    setSelectedSubmission(submission);
    setOpenSubmissionDetailDialog(true);
  };

  const handleSelectSubmission = (submissionId: string) => {
    const newSelected = new Set(selectedSubmissions);
    if (newSelected.has(submissionId)) {
      newSelected.delete(submissionId);
    } else {
      newSelected.add(submissionId);
    }
    setSelectedSubmissions(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedSubmissions.size === submissions.length) {
      setSelectedSubmissions(new Set());
    } else {
      setSelectedSubmissions(new Set(submissions.map((s) => s.id)));
    }
  };

  const generateToken = (): string => {
    return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
  };

  const generateShortUrl = (surveyId: string, token: string): string => {
    return `${window.location.origin}/survey/${token}/${surveyId}/${token}`;
  };

  const handleCreateLinkSubmit = () => {
    if (!linkFormData.surveyId || !linkFormData.surveyName) {
      return;
    }

    const token = generateToken();
    const newLink: SurveyLink = {
      id: 'link-' + Date.now(),
      surveyId: linkFormData.surveyId,
      surveyName: linkFormData.surveyName,
      token,
      url: generateShortUrl(linkFormData.surveyId, token),
      requiresAgentCode: linkFormData.requiresAgentCode,
      expiresAt: linkFormData.expiresAt || undefined,
      createdAt: new Date().toISOString(),
      submissionCount: 0,
      isActive: true,
    };

    setLinks([newLink, ...links]);
    setOpenLinkDialog(false);
    setLinkFormData({
      surveyId: '',
      surveyName: '',
      requiresAgentCode: false,
      expiresAt: '',
    });
  };

  const handleCopyLink = (url: string) => {
    navigator.clipboard.writeText(url);
    alert('Lien copié dans le presse-papiers !');
  };

  const handleDeleteLink = (linkId: string) => {
    setLinks(links.filter((l) => l.id !== linkId));
  };

  const handleExportSubmissions = (exportType: 'all' | 'selected' | 'filtered', format: 'excel' | 'csv' = exportFormat) => {
    let dataToExport: any[] = [];

    if (exportType === 'all') {
      dataToExport = submissions;
    } else if (exportType === 'selected') {
      dataToExport = submissions.filter((s) => selectedSubmissions.has(s.id));
    } else {
      // filtered - pour l'instant, même que all
      dataToExport = submissions;
    }

    if (dataToExport.length === 0) {
      alert('Aucune soumission à exporter');
      return;
    }

    // Préparer les données pour l'export
    const headers = ['ID', 'Sondage', 'Soumis par', 'Date', 'Localisation', 'Latitude', 'Longitude'];
    
    // Extraire toutes les clés des données pour les colonnes dynamiques
    const allDataKeys = new Set<string>();
    dataToExport.forEach((sub) => {
      if (sub.data && typeof sub.data === 'object') {
        Object.keys(sub.data).forEach((key) => allDataKeys.add(key));
      }
    });
    const dataKeys = Array.from(allDataKeys).sort();
    
    // Créer les en-têtes complets
    const fullHeaders = [...headers, ...dataKeys];

    // Préparer les lignes de données
    const rows = dataToExport.map((sub) => {
      const baseRow = [
        sub.id,
        sub.form_name,
        sub.submitted_by_name,
        new Date(sub.created_at).toLocaleString('fr-FR'),
        sub.location || '-',
        sub.latitude || '-',
        sub.longitude || '-',
      ];
      
      // Ajouter les valeurs des données dynamiques
      const dataValues = dataKeys.map((key) => {
        const value = sub.data?.[key];
        if (value === null || value === undefined) return '-';
        if (typeof value === 'object') return JSON.stringify(value);
        return String(value);
      });
      
      return [...baseRow, ...dataValues];
    });

    if (format === 'excel') {
      // Export Excel
      const worksheetData = [fullHeaders, ...rows];
      const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
      
      // Ajuster la largeur des colonnes
      const colWidths = fullHeaders.map((header, idx) => {
        const maxLength = Math.max(
          header.length,
          ...rows.map((row) => String(row[idx] || '').length)
        );
        return { wch: Math.min(Math.max(maxLength + 2, 10), 50) };
      });
      worksheet['!cols'] = colWidths;
      
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Soumissions');
      
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `soumissions_${new Date().toISOString().split('T')[0]}.xlsx`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      // Export CSV
      const csvContent = [
        fullHeaders.map((h) => `"${h}"`).join(','),
        ...rows.map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(',')),
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `soumissions_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }

    setOpenExportDialog(false);
    setSelectedSubmissions(new Set());
  };

  const isExpired = (expiresAt?: string): boolean => {
    if (!expiresAt) return false;
    return new Date(expiresAt) < new Date();
  };

  const canCreateSurvey = hasPermission('surveys:create');
  const canRespondToSurvey = hasPermission('surveys:respond');
  const canViewAnalytics = hasPermission('surveys:view_analytics');
  const canManageLinks = isAdmin || isAgence || isSOBEBRA;

  const getTotalSubmissions = () => {
    return submissions.length;
  };

  const getSubmissionsByForm = (formId: string) => {
    return submissions.filter((s) => s.form_id === formId).length;
  };

  const getRecentSubmissions = () => {
    return submissions.slice(0, 50);
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              <DataObjectIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Collecte de Données
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Gestion des formulaires de collecte, liens de sondage et suivi des soumissions
            </Typography>
          </Box>
          <Box>
            {canManageLinks && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleCreateLink()}
                sx={{ mr: 2 }}
              >
                Nouveau Lien
              </Button>
            )}
            {canViewAnalytics && (
              <Button
                variant="outlined"
                startIcon={<AnalyticsIcon />}
                onClick={() => navigate('/survey-analytics')}
              >
                Suivi des Sondages
              </Button>
            )}
          </Box>
        </Box>

        {/* Statistiques */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Sondages
                </Typography>
                <Typography variant="h4">{forms.length}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Soumissions
                </Typography>
                <Typography variant="h4" color="primary.main">
                  {getTotalSubmissions()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Liens Actifs
                </Typography>
                <Typography variant="h4" color="success.main">
                  {links.filter((l) => l.isActive && !isExpired(l.expiresAt)).length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Soumissions Aujourd'hui
                </Typography>
                <Typography variant="h4" color="info.main">
                  {submissions.filter((s) => {
                    const today = new Date();
                    const submissionDate = new Date(s.created_at);
                    return (
                      today.getDate() === submissionDate.getDate() &&
                      today.getMonth() === submissionDate.getMonth() &&
                      today.getFullYear() === submissionDate.getFullYear()
                    );
                  }).length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Onglets */}
        <Paper sx={{ mb: 3 }}>
          <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
            <Tab label="Sondages Actifs" />
            <Tab label="Soumissions" />
            <Tab label="Liens de Sondage" />
          </Tabs>
        </Paper>

        {/* Onglet Sondages Actifs */}
        {tabValue === 0 && (
          <TableContainer 
            component={Paper}
            sx={{
              overflowX: 'auto',
              '& .MuiTable-root': {
                minWidth: 500,
              }
            }}
          >
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Nom du Sondage</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Créé par</TableCell>
                  <TableCell>Soumissions</TableCell>
                  <TableCell>Date de création</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {forms.length === 0 ? (
                  <TableRow>
                    {/* @ts-ignore - colSpan is valid HTML attribute */}
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <Typography variant="body1" color="textSecondary">
                        Aucun sondage pour le moment
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  forms.map((form) => (
                    <TableRow key={form.id} hover>
                      <TableCell>
                        <Typography variant="subtitle2">{form.name}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="textSecondary">
                          {form.description || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>{form.created_by_name}</TableCell>
                      <TableCell>
                        <Chip
                          label={getSubmissionsByForm(form.id)}
                          color="primary"
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {new Date(form.created_at).toLocaleDateString('fr-FR')}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, form.id)}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Onglet Soumissions */}
        {tabValue === 1 && (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedSubmissions.size === submissions.length && submissions.length > 0}
                    indeterminate={selectedSubmissions.size > 0 && selectedSubmissions.size < submissions.length}
                    onChange={handleSelectAll}
                  />
                }
                label={`${selectedSubmissions.size} sélectionné(s)`}
              />
              <Box>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={() => setOpenExportDialog(true)}
                  disabled={selectedSubmissions.size === 0}
                  sx={{ mr: 1 }}
                >
                  Exporter ({selectedSubmissions.size})
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={() => handleExportSubmissions('all', 'excel')}
                >
                  Exporter Tout
                </Button>
              </Box>
            </Box>
            <TableContainer 
              component={Paper}
              sx={{
                overflowX: 'auto',
                '& .MuiTable-root': {
                  minWidth: 700,
                }
              }}
            >
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedSubmissions.size === submissions.length && submissions.length > 0}
                        indeterminate={selectedSubmissions.size > 0 && selectedSubmissions.size < submissions.length}
                        onChange={handleSelectAll}
                      />
                    </TableCell>
                    <TableCell>Sondage</TableCell>
                    <TableCell>Soumis par</TableCell>
                    <TableCell>Localisation</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {getRecentSubmissions().length === 0 ? (
                    <TableRow>
                      {/* @ts-ignore - colSpan is valid HTML attribute */}
                      <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                        <Typography variant="body1" color="textSecondary">
                          Aucune soumission pour le moment
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    getRecentSubmissions().map((submission) => (
                      <TableRow key={submission.id} hover>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedSubmissions.has(submission.id)}
                            onChange={() => handleSelectSubmission(submission.id)}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="subtitle2">
                            {submission.form_name}
                          </Typography>
                        </TableCell>
                        <TableCell>{submission.submitted_by_name}</TableCell>
                        <TableCell>
                          {submission.location ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <LocationOnIcon fontSize="small" color="action" />
                              <Typography variant="body2">{submission.location}</Typography>
                            </Box>
                          ) : (
                            '-'
                          )}
                        </TableCell>
                        <TableCell>
                          {new Date(submission.created_at).toLocaleString('fr-FR')}
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Voir les détails">
                            <IconButton
                              size="small"
                              onClick={() => handleViewSubmissionDetails(submission)}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Télécharger">
                            <IconButton
                              size="small"
                              onClick={() => handleExportSubmissions('selected', 'excel')}
                            >
                              <GetAppIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* Onglet Liens de Sondage */}
        {tabValue === 2 && (
          <Box>
            <TableContainer 
              component={Paper}
              sx={{
                overflowX: 'auto',
                '& .MuiTable-root': {
                  minWidth: 800,
                }
              }}
            >
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Sondage</TableCell>
                    <TableCell>Lien</TableCell>
                    <TableCell>Sécurité</TableCell>
                    <TableCell>Expiration</TableCell>
                    <TableCell>Soumissions</TableCell>
                    <TableCell>Statut</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {links.length === 0 ? (
                    <TableRow>
                      {/* @ts-ignore - colSpan is valid HTML attribute */}
                      <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                        <Typography variant="body1" color="textSecondary">
                          Aucun lien généré
                        </Typography>
                        {canManageLinks && (
                          <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => handleCreateLink()}
                            sx={{ mt: 2 }}
                          >
                            Générer un premier lien
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ) : (
                    links.map((link) => (
                      <TableRow key={link.id} hover>
                        <TableCell>
                          <Typography variant="body1" fontWeight="medium">
                            {link.surveyName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            ID: {link.surveyId}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontFamily: 'monospace',
                                fontSize: '0.75rem',
                                maxWidth: 200,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {link.url}
                            </Typography>
                            <Tooltip title="Copier le lien">
                              <IconButton
                                size="small"
                                onClick={() => handleCopyLink(link.url)}
                              >
                                <ContentCopyIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                        <TableCell>
                          {link.requiresAgentCode ? (
                            <Chip label="Code Agent" size="small" color="warning" />
                          ) : (
                            <Chip label="Public" size="small" color="success" />
                          )}
                        </TableCell>
                        <TableCell>
                          {link.expiresAt ? (
                            <Typography variant="body2">
                              {new Date(link.expiresAt).toLocaleDateString('fr-FR')}
                              {isExpired(link.expiresAt) && (
                                <Chip label="Expiré" size="small" color="error" sx={{ ml: 1 }} />
                              )}
                            </Typography>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              Sans expiration
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body1" fontWeight="bold">
                            {link.submissionCount}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {link.isActive ? (
                            <Chip label="Actif" size="small" color="success" />
                          ) : (
                            <Chip label="Inactif" size="small" color="default" />
                          )}
                        </TableCell>
                        <TableCell>
                          <Tooltip title="Voir QR Code">
                            <IconButton
                              size="small"
                              onClick={() => handleShowQR(link.id)}
                            >
                              <QrCodeIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Ouvrir le lien">
                            <IconButton
                              size="small"
                              onClick={() => window.open(link.url, '_blank')}
                            >
                              <VisibilityIcon />
                            </IconButton>
                          </Tooltip>
                          {canManageLinks && (
                            <Tooltip title="Supprimer">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteLink(link.id)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </Box>

      {/* Menu contextuel */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        {selectedFormId && (
          <>
            {canRespondToSurvey && (
              <MenuItem
                onClick={() => {
                  const form = forms.find((f) => f.id === selectedFormId);
                  if (form) {
                    const link = links.find((l) => l.surveyId === form.id);
                    if (link) {
                      window.open(link.url, '_blank');
                    }
                  }
                  handleMenuClose();
                }}
              >
                <VisibilityIcon sx={{ mr: 1 }} fontSize="small" />
                Répondre au sondage
              </MenuItem>
            )}
            {canManageLinks && (
              <MenuItem
                onClick={() => {
                  const form = forms.find((f) => f.id === selectedFormId);
                  if (form) handleCreateLink(form);
                }}
              >
                <LinkIcon sx={{ mr: 1 }} fontSize="small" />
                Créer un lien
              </MenuItem>
            )}
            {canManageLinks && (
              <MenuItem
                onClick={() => {
                  const form = forms.find((f) => f.id === selectedFormId);
                  if (form) {
                    const link = links.find((l) => l.surveyId === form.id);
                    if (link) handleShowQR(link.id);
                  }
                }}
              >
                <QrCodeIcon sx={{ mr: 1 }} fontSize="small" />
                Voir QR Code
              </MenuItem>
            )}
            {canViewAnalytics && (
              <MenuItem
                onClick={() => {
                  const form = forms.find((f) => f.id === selectedFormId);
                  if (form) handleViewAnalytics(form);
                }}
              >
                <AnalyticsIcon sx={{ mr: 1 }} fontSize="small" />
                Voir Analytics
              </MenuItem>
            )}
          </>
        )}
      </Menu>

      {/* Dialog Créer Lien */}
      <Dialog 
        open={openLinkDialog} 
        onClose={() => {
          setOpenLinkDialog(false);
          setCreateMode('select');
          setQuestionnaireData({ name: '', description: '', questions: [] });
        }} 
        maxWidth="md" 
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>Générer un nouveau lien de sondage</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Tabs value={createMode} onChange={(e, newValue) => setCreateMode(newValue)} sx={{ mb: 3 }}>
              <Tab label="Sélectionner un questionnaire existant" value="select" />
              <Tab label="Créer un nouveau questionnaire" value="create" />
            </Tabs>

            {createMode === 'select' ? (
              <Box>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Sondage</InputLabel>
                  <Select
                    label="Sondage"
                    value={linkFormData.surveyId}
                    onChange={(e) => {
                      const form = forms.find((f) => f.id === e.target.value);
                      setLinkFormData({
                        ...linkFormData,
                        surveyId: e.target.value,
                        surveyName: form?.name || '',
                      });
                    }}
                  >
                    {forms.map((form) => (
                      <MenuItem key={form.id} value={form.id}>
                        {form.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  fullWidth
                  label="Nom du Sondage"
                  margin="normal"
                  required
                  value={linkFormData.surveyName}
                  onChange={(e) => setLinkFormData({ ...linkFormData, surveyName: e.target.value })}
                />
              </Box>
            ) : (
              <Box>
                <TextField
                  fullWidth
                  label="Nom du questionnaire"
                  margin="normal"
                  required
                  value={questionnaireData.name}
                  onChange={(e) => setQuestionnaireData({ ...questionnaireData, name: e.target.value })}
                  helperText="Le nom du questionnaire qui apparaîtra dans la liste"
                />
                <TextField
                  fullWidth
                  label="Description"
                  margin="normal"
                  multiline
                  rows={2}
                  value={questionnaireData.description}
                  onChange={(e) => setQuestionnaireData({ ...questionnaireData, description: e.target.value })}
                  helperText="Description optionnelle du questionnaire"
                />
                
                <Divider sx={{ my: 3 }} />
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">Questions du questionnaire</Typography>
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      const newQuestion = {
                        id: `q-${Date.now()}`,
                        type: 'text' as const,
                        label: '',
                        required: false,
                      };
                      setQuestionnaireData({
                        ...questionnaireData,
                        questions: [...questionnaireData.questions, newQuestion],
                      });
                    }}
                  >
                    Ajouter une question
                  </Button>
                </Box>

                {questionnaireData.questions.length === 0 ? (
                  <Alert severity="info">
                    Aucune question ajoutée. Cliquez sur "Ajouter une question" pour commencer.
                  </Alert>
                ) : (
                  <Box sx={{ maxHeight: '400px', overflowY: 'auto' }}>
                    {questionnaireData.questions.map((question, index) => (
                      <Paper key={question.id} sx={{ p: 2, mb: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                            Question {index + 1}
                          </Typography>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => {
                              setQuestionnaireData({
                                ...questionnaireData,
                                questions: questionnaireData.questions.filter((q) => q.id !== question.id),
                              });
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                        
                        <FormControl fullWidth sx={{ mb: 2 }}>
                          <InputLabel>Type de question</InputLabel>
                          <Select
                            value={question.type}
                            label="Type de question"
                            onChange={(e) => {
                              const updatedQuestions = [...questionnaireData.questions];
                              updatedQuestions[index] = {
                                ...question,
                                type: e.target.value as any,
                                options: (e.target.value === 'select' || e.target.value === 'radio') ? ['Option 1', 'Option 2'] : undefined,
                              };
                              setQuestionnaireData({ ...questionnaireData, questions: updatedQuestions });
                            }}
                          >
                            <MenuItem value="text">Texte court</MenuItem>
                            <MenuItem value="textarea">Texte long</MenuItem>
                            <MenuItem value="number">Nombre</MenuItem>
                            <MenuItem value="select">Liste déroulante</MenuItem>
                            <MenuItem value="radio">Choix unique</MenuItem>
                            <MenuItem value="checkbox">Choix multiples</MenuItem>
                          </Select>
                        </FormControl>

                        <TextField
                          fullWidth
                          label="Libellé de la question"
                          margin="normal"
                          required
                          value={question.label}
                          onChange={(e) => {
                            const updatedQuestions = [...questionnaireData.questions];
                            updatedQuestions[index] = { ...question, label: e.target.value };
                            setQuestionnaireData({ ...questionnaireData, questions: updatedQuestions });
                          }}
                        />

                        {(question.type === 'text' || question.type === 'number' || question.type === 'textarea') && (
                          <TextField
                            fullWidth
                            label="Placeholder (optionnel)"
                            margin="normal"
                            value={question.placeholder || ''}
                            onChange={(e) => {
                              const updatedQuestions = [...questionnaireData.questions];
                              updatedQuestions[index] = { ...question, placeholder: e.target.value };
                              setQuestionnaireData({ ...questionnaireData, questions: updatedQuestions });
                            }}
                          />
                        )}

                        {(question.type === 'select' || question.type === 'radio' || question.type === 'checkbox') && (
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="body2" sx={{ mb: 1 }}>Options de réponse</Typography>
                            {(question.options || []).map((option, optIndex) => (
                              <Box key={optIndex} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  value={option}
                                  onChange={(e) => {
                                    const updatedQuestions = [...questionnaireData.questions];
                                    const updatedOptions = [...(question.options || [])];
                                    updatedOptions[optIndex] = e.target.value;
                                    updatedQuestions[index] = { ...question, options: updatedOptions };
                                    setQuestionnaireData({ ...questionnaireData, questions: updatedQuestions });
                                  }}
                                />
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    const updatedQuestions = [...questionnaireData.questions];
                                    const updatedOptions = (question.options || []).filter((_, i) => i !== optIndex);
                                    updatedQuestions[index] = { ...question, options: updatedOptions };
                                    setQuestionnaireData({ ...questionnaireData, questions: updatedQuestions });
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            ))}
                            <Button
                              size="small"
                              startIcon={<AddIcon />}
                              onClick={() => {
                                const updatedQuestions = [...questionnaireData.questions];
                                const updatedOptions = [...(question.options || []), `Option ${(question.options || []).length + 1}`];
                                updatedQuestions[index] = { ...question, options: updatedOptions };
                                setQuestionnaireData({ ...questionnaireData, questions: updatedQuestions });
                              }}
                            >
                              Ajouter une option
                            </Button>
                          </Box>
                        )}

                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={question.required}
                              onChange={(e) => {
                                const updatedQuestions = [...questionnaireData.questions];
                                updatedQuestions[index] = { ...question, required: e.target.checked };
                                setQuestionnaireData({ ...questionnaireData, questions: updatedQuestions });
                              }}
                            />
                          }
                          label="Question obligatoire"
                          sx={{ mt: 1 }}
                        />
                      </Paper>
                    ))}
                  </Box>
                )}
              </Box>
            )}

            <Divider sx={{ my: 3 }} />

            <FormControlLabel
              control={
                <Switch
                  checked={linkFormData.requiresAgentCode}
                  onChange={(e) => setLinkFormData({ ...linkFormData, requiresAgentCode: e.target.checked })}
                />
              }
              label="Nécessite un code agent (accès sécurisé)"
              sx={{ mt: 2 }}
            />
            <TextField
              fullWidth
              label="Date d'expiration (optionnel)"
              margin="normal"
              type="datetime-local"
              InputLabelProps={{ shrink: true }}
              value={linkFormData.expiresAt}
              onChange={(e) => setLinkFormData({ ...linkFormData, expiresAt: e.target.value })}
              helperText="Le lien sera désactivé après cette date"
            />
            <Alert severity="info" sx={{ mt: 2 }}>
              Un QR Code sera généré automatiquement pour faciliter le déploiement sur le terrain.
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setOpenLinkDialog(false);
            setCreateMode('select');
            setQuestionnaireData({ name: '', description: '', questions: [] });
          }}>
            Annuler
          </Button>
          <Button
            onClick={async () => {
              if (createMode === 'create') {
                // Créer le questionnaire d'abord
                if (!questionnaireData.name || questionnaireData.questions.length === 0) {
                  alert('Veuillez remplir le nom du questionnaire et ajouter au moins une question');
                  return;
                }

                // Vérifier que toutes les questions ont un libellé
                const invalidQuestions = questionnaireData.questions.filter(q => !q.label.trim());
                if (invalidQuestions.length > 0) {
                  alert('Toutes les questions doivent avoir un libellé');
                  return;
                }

                try {
                  // Convertir les questions au format form_schema
                  const formSchema = {
                    fields: questionnaireData.questions.map((q) => ({
                      name: `field_${q.id}`,
                      type: q.type,
                      label: q.label,
                      required: q.required,
                      placeholder: q.placeholder,
                      options: q.options,
                    })),
                  };

                  const response = await api.forms.create({
                    name: questionnaireData.name,
                    description: questionnaireData.description || undefined,
                    form_schema: formSchema,
                  });

                  // Utiliser le formulaire créé pour générer le lien
                  setLinkFormData({
                    ...linkFormData,
                    surveyId: response.form.id,
                    surveyName: response.form.name,
                  });

                  // Recharger les formulaires
                  await fetchForms();

                  // Générer le lien
                  handleCreateLinkSubmit();
                } catch (error: any) {
                  console.error('Erreur lors de la création du questionnaire:', error);
                  alert('Erreur lors de la création du questionnaire: ' + (error.message || 'Erreur inconnue'));
                }
              } else {
                // Mode sélection : générer directement le lien
                handleCreateLinkSubmit();
              }
            }}
            variant="contained"
            disabled={
              createMode === 'select' 
                ? (!linkFormData.surveyId || !linkFormData.surveyName)
                : (!questionnaireData.name || questionnaireData.questions.length === 0)
            }
          >
            {createMode === 'create' ? 'Créer et générer le lien' : 'Générer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog QR Code */}
      <Dialog 
        open={openQRDialog !== null} 
        onClose={() => setOpenQRDialog(null)} 
        maxWidth="xs" 
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>QR Code</DialogTitle>
        <DialogContent>
          {openQRDialog && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 2 }}>
              {(() => {
                const link = links.find((l) => l.id === openQRDialog);
                return link ? (
                  <>
                    <QRCodeSVG value={link.url} size={200} />
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
                      Scannez ce QR Code pour accéder au questionnaire
                    </Typography>
                    <TextField
                      fullWidth
                      value={link.url}
                      InputProps={{
                        readOnly: true,
                      }}
                      sx={{ mt: 2 }}
                      onFocus={(e) => e.target.select()}
                    />
                    <Button
                      startIcon={<ContentCopyIcon />}
                      onClick={() => handleCopyLink(link.url)}
                      sx={{ mt: 2 }}
                    >
                      Copier le lien
                    </Button>
                  </>
                ) : null;
              })()}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenQRDialog(null)}>Fermer</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Détails Soumission */}
      <Dialog
        open={openSubmissionDetailDialog}
        onClose={() => setOpenSubmissionDetailDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Détails de la Soumission
          {selectedSubmission && (
            <Chip
              label={selectedSubmission.form_name}
              color="primary"
              size="small"
              sx={{ ml: 2 }}
            />
          )}
        </DialogTitle>
        <DialogContent>
          {selectedSubmission && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Soumis par
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {selectedSubmission.submitted_by_name}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Date de soumission
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {new Date(selectedSubmission.created_at).toLocaleString('fr-FR')}
                  </Typography>
                </Grid>
                {selectedSubmission.location && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Localisation
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LocationOnIcon color="action" />
                      <Typography variant="body1">{selectedSubmission.location}</Typography>
                    </Box>
                    {selectedSubmission.latitude && selectedSubmission.longitude && (
                      <Typography variant="caption" color="textSecondary" sx={{ ml: 4 }}>
                        Coordonnées: {selectedSubmission.latitude}, {selectedSubmission.longitude}
                      </Typography>
                    )}
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    Données collectées
                  </Typography>
                  <Paper sx={{ p: 2, bgcolor: 'grey.50', maxHeight: 400, overflow: 'auto' }}>
                    <pre style={{ margin: 0, fontSize: '0.875rem' }}>
                      {JSON.stringify(selectedSubmission.data, null, 2)}
                    </pre>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenSubmissionDetailDialog(false)}>Fermer</Button>
          <Button
            variant="contained"
            startIcon={<GetAppIcon />}
            onClick={() => {
              // Export Excel pour une soumission individuelle
              const headers = ['Champ', 'Valeur'];
              const rows = Object.entries(selectedSubmission.data).map(([key, value]) => [
                key,
                typeof value === 'object' ? JSON.stringify(value) : String(value),
              ]);
              
              const worksheetData = [headers, ...rows];
              const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
              
              // Ajuster la largeur des colonnes
              worksheet['!cols'] = [{ wch: 20 }, { wch: 50 }];
              
              const workbook = XLSX.utils.book_new();
              XLSX.utils.book_append_sheet(workbook, worksheet, 'Soumission');
              
              const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
              const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
              const link = document.createElement('a');
              const url = URL.createObjectURL(blob);
              link.setAttribute('href', url);
              link.setAttribute('download', `soumission_${selectedSubmission.id}.xlsx`);
              link.style.visibility = 'hidden';
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            }}
          >
            Télécharger
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Export */}
      <Dialog 
        open={openExportDialog} 
        onClose={() => setOpenExportDialog(false)} 
        maxWidth="sm" 
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>Exporter les Soumissions</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body1" gutterBottom>
              {selectedSubmissions.size} soumission(s) sélectionnée(s)
            </Typography>
            
            {/* Sélecteur de format */}
            <FormControl fullWidth sx={{ mt: 2, mb: 2 }}>
              <InputLabel>Format d'export</InputLabel>
              <Select
                value={exportFormat}
                label="Format d'export"
                onChange={(e) => setExportFormat(e.target.value as 'excel' | 'csv')}
              >
                <MenuItem value="excel">Excel (.xlsx) - Par défaut</MenuItem>
                <MenuItem value="csv">CSV (.csv)</MenuItem>
              </Select>
            </FormControl>
            
            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Button
                variant="contained"
                fullWidth
                startIcon={<DownloadIcon />}
                onClick={() => handleExportSubmissions('selected', exportFormat)}
                disabled={selectedSubmissions.size === 0}
              >
                Exporter les sélectionnées ({selectedSubmissions.size}) - {exportFormat.toUpperCase()}
              </Button>
              <Button
                variant="outlined"
                fullWidth
                startIcon={<DownloadIcon />}
                onClick={() => handleExportSubmissions('all', exportFormat)}
              >
                Exporter toutes les soumissions ({submissions.length}) - {exportFormat.toUpperCase()}
              </Button>
            </Box>
            <Alert severity="info" sx={{ mt: 2 }}>
              Les données seront exportées au format <strong>{exportFormat === 'excel' ? 'Excel (.xlsx)' : 'CSV (.csv)'}</strong> avec toutes les informations (données, localisation, dates).
              {exportFormat === 'excel' && ' Le format Excel est recommandé pour une meilleure lisibilité et manipulation des données.'}
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenExportDialog(false)}>Fermer</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default DataCollection;
